import React, { useState, useMemo } from 'react';
import { ReportData } from '../../types/report.types';
import { getAttestationData } from '../../utils/attestationUtils';
import { pdf, Document } from '@react-pdf/renderer';
import LetterOfAttestationPDF from '../sections/LetterOfAttestationPDF';
import { CTB_LOGO_DARK_BASE64 } from '../../../../assets/ctbLogoDarkBase64';
import { FiDownload, FiRotateCcw, FiFileText, FiChevronDown, FiChevronUp } from 'react-icons/fi';

interface AttestationEditorProps {
  reportData: ReportData;
  onDataChange: (field: string, value: any) => void;
}

export const AttestationEditor: React.FC<AttestationEditorProps> = ({
  reportData,
  onDataChange
}) => {
  const [downloading, setDownloading] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(true);

  // Parse existing finding_config
  let parsedConfig: any = {};
  if (reportData?.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }

  const loaConfig: any = parsedConfig.attestation_letter || parsedConfig.certificate || {};

  // Extract merged data (defaults + overrides)
  const currentLoa = useMemo(() => {
    return getAttestationData(reportData);
  }, [reportData]);

  // Update a field in finding_config
  const handleUpdate = (field: string, value: any) => {
    const updatedLoa = {
      ...loaConfig,
      [field]: value
    };
    onDataChange('finding_config', {
      ...parsedConfig,
      attestation_letter: updatedLoa,
      certificate: updatedLoa // keep in sync for backward compatibility
    });
  };

  // Reset all overrides to auto-populated engagement values
  const handleReset = () => {
    const cleaned = { ...parsedConfig };
    delete cleaned.attestation_letter;
    delete cleaned.certificate;
    onDataChange('finding_config', cleaned);
  };

  // Direct download of the Letter of Attestation PDF
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const doc = (
        <Document>
          <LetterOfAttestationPDF reportData={reportData} />
        </Document>
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const clientSafe = (currentLoa.client_name || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.download = `${clientSafe}-Letter-of-Attestation.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating Letter of Attestation PDF:', err);
      alert('Error generating PDF. Please check your data and try again.');
    } finally {
      setDownloading(false);
    }
  };

  const assessmentTypePresets = [
    'Web Application Penetration Test',
    'API Penetration Test',
    'Mobile Application (iOS/Android) Penetration Test',
    'External Network Penetration Test',
    'Internal Network Penetration Test',
    'AI/LLM Security Assessment',
    'Cloud Security Assessment',
  ];

  const approachPresets = ['Graybox', 'Blackbox', 'Whitebox'];

  return (
    <div className="h-full overflow-y-auto space-y-4 pb-12">
      {/* Compact Header Banner */}
      <div className="bg-white rounded-xl shadow-xs border border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0">
              <FiFileText className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-slate-900 truncate">Letter of Attestation (LOA) Editor</h3>
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                  1-Page PDF
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border shrink-0 ${
                  currentLoa.enabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}>
                  {currentLoa.enabled ? '✓ Enabled' : 'Off by Default'}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 truncate mt-0.5">
                Review and customize the fields printed on the official CTB Letter of Attestation.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition shadow-xs"
              title="Reset all overrides to original engagement values"
            >
              <FiRotateCcw className="w-3.5 h-3.5 text-slate-500" />
              <span>Reset</span>
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-xs transition disabled:opacity-60"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FiDownload className="w-3.5 h-3.5" />
                  <span>Download LOA (PDF)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Master Enable/Disable Attestation Letter Toggle Card */}
      <div className={`rounded-xl border transition-all p-3.5 sm:p-4 shadow-xs ${
        currentLoa.enabled
          ? 'bg-blue-50/70 border-blue-200'
          : 'bg-amber-50/60 border-amber-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start sm:items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer select-none mt-0.5 sm:mt-0 shrink-0">
              <input
                type="checkbox"
                id="loa_master_toggle"
                checked={!!currentLoa.enabled}
                onChange={(e) => handleUpdate('enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-10 h-5.5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4.5 after:w-4.5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm font-bold text-slate-900">
                  Enable Letter of Attestation
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${
                  currentLoa.enabled
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : 'bg-amber-100 text-amber-800 border-amber-300'
                }`}>
                  {currentLoa.enabled ? '✓ Enabled' : 'Off (Default)'}
                </span>
              </div>
              <p className="text-[11px] text-slate-600 mt-0.5">
                {currentLoa.enabled
                  ? 'Letter of Attestation is active and will be available for business download once the report is approved.'
                  : 'Currently off. QA or Admin must toggle this on to make the Letter of Attestation available to the business user.'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleUpdate('enabled', !currentLoa.enabled)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition shrink-0 ${
              currentLoa.enabled
                ? 'bg-white border-blue-300 text-blue-700 hover:bg-blue-50'
                : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 shadow-xs'
            }`}
          >
            {currentLoa.enabled ? 'Turn Off' : 'Turn On'}
          </button>
        </div>
      </div>

      {/* Main Form Fields Card */}
      <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-5 space-y-4">
        {/* Section 1: Letter Meta & Header */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Letter Header & Identification
              </span>
            </div>
            <span className="text-[10px] text-slate-400">Header info</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
            <div className="sm:col-span-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Letter Date
              </label>
              <input
                type="text"
                value={currentLoa.date}
                onChange={(e) => handleUpdate('date', e.target.value)}
                placeholder="e.g. 15 Oct 2026"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
            </div>
            <div className="sm:col-span-6">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Reference Code
              </label>
              <input
                type="text"
                value={currentLoa.reference}
                onChange={(e) => handleUpdate('reference', e.target.value)}
                placeholder="CTB-TECH-CYBER-XXXX-V.01"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 font-mono"
              />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Report Date
              </label>
              <input
                type="text"
                value={currentLoa.report_date}
                onChange={(e) => handleUpdate('report_date', e.target.value)}
                placeholder="e.g. 15 Oct 2026"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Engagement Details & Scope */}
        <div className="space-y-2.5 pt-1">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Engagement Details & Scope
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Client / Organisation Name
              </label>
              <input
                type="text"
                value={currentLoa.client_name}
                onChange={(e) => handleUpdate('client_name', e.target.value)}
                placeholder="e.g. Acme Corporation Ltd"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Testing Window
              </label>
              <input
                type="text"
                value={currentLoa.testing_window}
                onChange={(e) => handleUpdate('testing_window', e.target.value)}
                placeholder="e.g. 15 – 22 Oct 2026"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Assessment Type
              </label>
              <input
                type="text"
                value={currentLoa.assessment_type}
                onChange={(e) => handleUpdate('assessment_type', e.target.value)}
                list="assessment_type_presets"
                placeholder="e.g. Web Application Penetration Test"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
              <datalist id="assessment_type_presets">
                {assessmentTypePresets.map((preset) => (
                  <option key={preset} value={preset} />
                ))}
              </datalist>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-slate-700">
                  Approach
                </label>
                <div className="inline-flex rounded-md p-0.5 bg-slate-100 border border-slate-200">
                  {approachPresets.map((preset) => {
                    const isSelected = currentLoa.approach?.trim().toLowerCase() === preset.toLowerCase();
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => handleUpdate('approach', preset)}
                        className={`px-2 py-0.5 text-[10px] rounded transition font-medium ${
                          isSelected
                            ? 'bg-blue-600 text-white font-semibold shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {preset}
                      </button>
                    );
                  })}
                </div>
              </div>
              <input
                type="text"
                value={currentLoa.approach}
                onChange={(e) => handleUpdate('approach', e.target.value)}
                placeholder="e.g. Graybox"
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Scope / Target Assets
              </label>
              <textarea
                value={currentLoa.scope}
                onChange={(e) => handleUpdate('scope', e.target.value)}
                placeholder="e.g. https://app.example.com, api.example.com"
                rows={2}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Findings Summary Counts */}
        <div className="bg-slate-50/70 rounded-lg border border-slate-200/80 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
              Findings Summary Counts
            </span>
            <span className="text-[10px] text-slate-400">Auto-populated from report</span>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {/* Total */}
            <div className="bg-white rounded-lg border border-slate-200 p-1.5 text-center shadow-xs">
              <div className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">Total</div>
              <input
                type="number"
                min="0"
                value={currentLoa.total_findings}
                onChange={(e) => handleUpdate('total_findings', parseInt(e.target.value, 10) || 0)}
                className="w-full text-center font-bold text-xs py-0.5 text-slate-900 bg-transparent focus:outline-none"
              />
            </div>
            {/* Critical */}
            <div className="bg-red-50/70 rounded-lg border border-red-200 p-1.5 text-center shadow-xs">
              <div className="text-[10px] font-bold text-red-700 uppercase tracking-wide">Critical</div>
              <input
                type="number"
                min="0"
                value={currentLoa.critical_count}
                onChange={(e) => handleUpdate('critical_count', parseInt(e.target.value, 10) || 0)}
                className="w-full text-center font-bold text-xs py-0.5 text-red-800 bg-transparent focus:outline-none"
              />
            </div>
            {/* High */}
            <div className="bg-orange-50/70 rounded-lg border border-orange-200 p-1.5 text-center shadow-xs">
              <div className="text-[10px] font-bold text-orange-700 uppercase tracking-wide">High</div>
              <input
                type="number"
                min="0"
                value={currentLoa.high_count}
                onChange={(e) => handleUpdate('high_count', parseInt(e.target.value, 10) || 0)}
                className="w-full text-center font-bold text-xs py-0.5 text-orange-800 bg-transparent focus:outline-none"
              />
            </div>
            {/* Medium */}
            <div className="bg-amber-50/70 rounded-lg border border-amber-200 p-1.5 text-center shadow-xs">
              <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Medium</div>
              <input
                type="number"
                min="0"
                value={currentLoa.medium_count}
                onChange={(e) => handleUpdate('medium_count', parseInt(e.target.value, 10) || 0)}
                className="w-full text-center font-bold text-xs py-0.5 text-amber-800 bg-transparent focus:outline-none"
              />
            </div>
            {/* Low */}
            <div className="bg-emerald-50/70 rounded-lg border border-emerald-200 p-1.5 text-center shadow-xs">
              <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">Low</div>
              <input
                type="number"
                min="0"
                value={currentLoa.low_count}
                onChange={(e) => handleUpdate('low_count', parseInt(e.target.value, 10) || 0)}
                className="w-full text-center font-bold text-xs py-0.5 text-emerald-800 bg-transparent focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Section 4: Remediation Verified Block (Optional) */}
        <div className={`rounded-lg border transition p-3 ${
          currentLoa.remediation_verified ? 'bg-emerald-50/50 border-emerald-200' : 'bg-slate-50/60 border-slate-200'
        }`}>
          <div className="flex items-center justify-between">
            <label htmlFor="remediation_toggle" className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                id="remediation_toggle"
                checked={currentLoa.remediation_verified}
                onChange={(e) => handleUpdate('remediation_verified', e.target.checked)}
                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
              />
              <span className="text-xs font-bold text-slate-800">
                Include "Remediation verified" block
              </span>
            </label>
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
              currentLoa.remediation_verified
                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                : 'bg-slate-200/70 text-slate-600 border-slate-300'
            }`}>
              {currentLoa.remediation_verified ? '✓ Shown on PDF' : 'Omitted'}
            </span>
          </div>

          {currentLoa.remediation_verified && (
            <div className="pt-2.5 mt-2 border-t border-emerald-200/80 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="block text-[11px] font-semibold text-emerald-950 mb-1">
                  Retest Date
                </label>
                <input
                  type="text"
                  value={currentLoa.retest_date}
                  onChange={(e) => handleUpdate('retest_date', e.target.value)}
                  placeholder="e.g. 15 Oct 2026"
                  className="w-full px-2.5 py-1.5 text-xs border border-emerald-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500/20 bg-white text-slate-800"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-semibold text-emerald-950 mb-1">
                  Custom Verification Text (Optional override)
                </label>
                <input
                  type="text"
                  value={currentLoa.remediation_text || ''}
                  onChange={(e) => handleUpdate('remediation_text', e.target.value)}
                  placeholder={`A verification retest was performed on ${currentLoa.retest_date || '...'}. All findings rated Critical and High have been remediated and confirmed closed.`}
                  className="w-full px-2.5 py-1.5 text-xs border border-emerald-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500/20 bg-white text-slate-800"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Collapsible Live Document Preview Card */}
      <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowLivePreview(!showLivePreview)}
          className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-left transition select-none"
        >
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Live Document Preview (White Theme)
            </span>
            <span className="text-[10px] text-slate-400 font-normal">1:1 Replica</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
            <span>{showLivePreview ? 'Collapse Preview' : 'Show Preview'}</span>
            {showLivePreview ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
          </div>
        </button>

        {showLivePreview && (
          <div className="p-4 sm:p-6 bg-slate-100/70 border-t border-slate-200">
            {/* Mockup Preview Sheet - Pristine White Paper Presentation */}
            <div className="border border-slate-200/90 rounded-xl bg-white p-8 sm:p-11 max-w-2xl mx-auto shadow-md text-slate-800 font-sans text-[13px] leading-relaxed">
              {/* Header */}
              <div className="flex items-start justify-between">
                <img src={CTB_LOGO_DARK_BASE64} alt="Capture The Bug" className="h-11 object-contain" />
                <div className="text-right text-xs leading-snug">
                  <p className="font-bold text-slate-900">Capture The Bug Limited</p>
                  <p className="text-slate-500">Newmarket Innovation Precinct, Level 2 B903,</p>
                  <p className="text-slate-500">298 Khyber Pass Road, Newmarket, Auckland 1023</p>
                  <p className="text-[#007BFC] font-semibold mt-0.5">capturethebug.xyz</p>
                </div>
              </div>

              {/* CTB Blue Divider */}
              <div className="h-0.5 bg-[#007BFC] my-4" />

              {/* Titles */}
              <div className="mb-3.5">
                <h2 className="text-lg font-bold text-[#00238C] tracking-wider uppercase">
                  LETTER OF ATTESTATION
                </h2>
                <p className="text-xs font-bold text-[#007BFC] mt-0.5">Penetration Testing Engagement</p>
              </div>

              {/* Date & Ref */}
              <div className="flex justify-between text-xs text-slate-700 mb-3.5">
                <p><span className="font-bold text-slate-900">Date:</span> {currentLoa.date}</p>
                <p><span className="font-bold text-slate-900">Reference:</span> {currentLoa.reference}</p>
              </div>

              <p className="mb-2.5 text-slate-900 font-semibold text-[13px]">To whom it may concern,</p>

              <p className="mb-3.5 text-slate-700 leading-relaxed text-[13px]">
                This letter confirms that <strong className="text-slate-900 font-bold">Capture The Bug Limited</strong>, a CREST-certified penetration testing provider, was engaged by <strong className="text-slate-900 font-bold">{currentLoa.client_name}</strong> to perform an independent penetration test of the systems described below.
              </p>

              {/* Scope / Details List - Clean unbordered 2-column layout matching mockup */}
              <div className="my-3.5 space-y-1.5 text-[13px]">
                <div className="flex items-start">
                  <span className="w-36 font-bold text-slate-900 shrink-0">Client</span>
                  <span className="text-slate-800">{currentLoa.client_name}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-36 font-bold text-slate-900 shrink-0">Assessment type</span>
                  <span className="text-slate-800">{currentLoa.assessment_type}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-36 font-bold text-slate-900 shrink-0">Approach</span>
                  <span className="text-slate-800">{currentLoa.approach}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-36 font-bold text-slate-900 shrink-0">Scope</span>
                  <span className="text-slate-800">{currentLoa.scope}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-36 font-bold text-slate-900 shrink-0">Testing window</span>
                  <span className="text-slate-800">{currentLoa.testing_window}</span>
                </div>
              </div>

              <p className="mb-3 text-slate-700 leading-relaxed text-[13px]">
                The assessment was conducted in line with industry-recognised methodologies, including the OWASP Testing Guides and the OWASP Top 10 (2021), as applicable to the engagement. Identified issues were manually validated and rated for severity. The detailed technical findings and remediation guidance were delivered to <strong className="text-slate-900 font-bold">{currentLoa.client_name}</strong> in a separate confidential report dated <strong className="text-slate-900 font-bold">{currentLoa.report_date}</strong>.
              </p>

              <p className="mb-3.5 text-slate-700 leading-relaxed text-[13px]">
                During the engagement, Capture The Bug identified <strong className="text-slate-900 font-bold">{currentLoa.total_findings}</strong> issues across the in-scope assets (<strong className="text-red-600 font-bold">{currentLoa.critical_count} Critical</strong>, <strong className="text-orange-600 font-bold">{currentLoa.high_count} High</strong>, <strong className="text-amber-600 font-bold">{currentLoa.medium_count} Medium</strong>, <strong className="text-emerald-600 font-bold">{currentLoa.low_count} Low</strong>). These findings were reported to <strong className="text-slate-900 font-bold">{currentLoa.client_name}</strong> for remediation.
              </p>

              {/* Remediation verified callout */}
              {currentLoa.remediation_verified && (
                <div className="bg-[#F0FDF4] border border-[#22C55E] rounded-md p-3 my-3 text-[#14532D] text-xs flex items-start gap-2.5 shadow-xs">
                  <svg className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <div className="leading-relaxed">
                    <strong className="text-[#15803D] font-bold">Remediation verified: </strong>
                    {currentLoa.remediation_text || `A verification retest was performed on ${currentLoa.retest_date}. All findings rated Critical and High have been remediated and confirmed closed.`}
                  </div>
                </div>
              )}

              <p className="text-[13px] text-slate-700 mb-3.5 leading-relaxed">
                This attestation reflects the security posture of the in-scope systems <strong className="text-slate-900 font-bold">at the time of testing only</strong>. A penetration test is a point-in-time assessment and cannot guarantee that all vulnerabilities have been identified. This letter does not constitute a certification, warranty, or guarantee of security, and the full technical report remains confidential between the parties.
              </p>

              <p className="text-[13px] text-slate-700 mb-4 leading-relaxed">
                For verification of this attestation, please contact Capture The Bug at <a href="mailto:hello@capturethebug.xyz" className="text-[#007BFC] font-semibold hover:underline">hello@capturethebug.xyz</a>.
              </p>

              <div className="mb-5">
                <p className="font-bold text-slate-900 text-sm">Issued by Capture The Bug Limited</p>
                <p className="text-xs italic text-slate-500 mt-0.5">This is a system-generated attestation and is valid without a physical signature.</p>
              </div>

              <div className="border-t border-slate-200 pt-3 text-center text-[11px] text-slate-400">
                Capture The Bug Limited · CREST-certified penetration testing · Confidential
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttestationEditor;
